/* easygusto Bottega : « Ma semaine italienne » (16 octobre 2026). Le client planifie sa semaine de recettes et remplit
   son panier en un geste.
   Module ES chargé par trois sections (une seule instance par page : même adresse versionnée) :
   - eg-recette (fiche, types « recette » et « recette_ia ») : bouton [data-eg-sem-act] « Ajouter à ma semaine »,
     qui devient « Dans ma semaine ✓ » (un second clic retire la recette), et lien [data-eg-sem-voir] « Ma semaine (n) » ;
   - eg-recettes (page Recettes) : lien d'en-tête [data-eg-sem-lien] « Ma semaine (n) » ;
   - eg-semaine (modèle page.semaine, adresse /pages/recettes?view=semaine) : recettes de la semaine (jour, portions,
     retrait), liste de courses agrégée, « Tout ajouter au panier », « Imprimer la liste ».
   Mémoire : localStorage « eg-semaine » = {v: 1, r: [{h, type, url, titre, img?, p, j?}]}, MAX recettes au plus
   (h = handle, type = recette | recette_ia, url = chemin de la fiche, p = portions, j = jour 0 à 6). Rien d'autre :
   aucune donnée personnelle, aucune contrainte de santé, aucun panier. Lecture et écriture sous try/catch : sans
   stockage, le bouton de la fiche reste masqué et la page le dit.
   Règles fermes (comme la fiche, lot L3b) :
   - le panier n'est appelé que par « Tout ajouter » (EG.addMany) et par son « Annuler » (EG.setQty), au clic ;
   - rien n'est inventé : chaque recette est relue sur sa fiche (même origine : Section Rendering API, sinon la page
     entière), dont on lit l'état [data-rcia-etat] rendu par le Liquid (lignes, contenances, variantes, prix, stock) ;
     une fiche qui répond 404 (recette supprimée) est retirée de la semaine, avec un message ;
   - quantités : quantite(), texteQte(), blocDe() de eg-rcia-fiche.js, importé à l'adresse versionnée donnée par la
     section (une seule source pour les arrondis) ;
   - texte venu de l'état, de la mémoire ou des traductions : textContent uniquement.
   Il ne s'initialise que si document existe : node --test importe les fonctions pures exportées. */

export const VERSION = '1.0.0';
export const CLE = 'eg-semaine';
export const SCHEMA = 1;
export const MAX = 7;
export const PORTIONS_MIN = 1;
export const PORTIONS_MAX = 16;
export const PORTIONS_DEFAUT = 4;
export const PAQUETS_MAX = 99;
export const JOURS = 7;
export const TYPES = ['recette', 'recette_ia'];
export const TITRE_MAX = 160;
export const H_RE = /^[a-z0-9][a-z0-9-]{0,99}$/;
/* Chemin d'une fiche de métaobjet : /pages/<type>/<handle>, avec un éventuel préfixe de langue (/es/, /en-gb/). */
export const URL_RE = /^\/(?:[a-z]{2}(?:-[a-z0-9]{2,4})?\/)?pages\/[a-z0-9][a-z0-9-]{0,63}\/[a-z0-9][a-z0-9-]{0,99}$/i;
const IMG_RE = /^https:\/\/[^\s"'<>\\]{1,600}$/;
/* Feuille d'impression : sans @page propre (la règle vaudrait pour toute la boutique), donc avec les marges par défaut
   du navigateur ; largeur et hauteur utiles prudentes pour un A4. */
export const A4 = { largeurMm: 180, hauteurMm: 255 };
export const REDUCTIONS = ['eg-sem-rp-dense', 'eg-sem-rp-xdense'];

/* ---------- Mémoire (fonctions pures) ---------- */

function entier(v, min, max) {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
  const n = Number(v);
  return Number.isInteger(n) && n >= min && n <= max ? n : null;
}

export function vide() { return { v: SCHEMA, r: [] }; }

/* Entrée valide ou null. Portions hors limites : portions par défaut ; jour hors 0 à 6 : pas de jour. */
export function nettoyer(e) {
  if (!e || typeof e !== 'object') return null;
  const h = typeof e.h === 'string' ? e.h : '';
  if (!H_RE.test(h) || TYPES.indexOf(e.type) < 0) return null;
  if (typeof e.url !== 'string' || !URL_RE.test(e.url)) return null;
  const titre = typeof e.titre === 'string' ? e.titre.replace(/\s+/g, ' ').trim().slice(0, TITRE_MAX) : '';
  const out = { h, type: e.type, url: e.url, titre, p: entier(e.p, PORTIONS_MIN, PORTIONS_MAX) || PORTIONS_DEFAUT };
  if (typeof e.img === 'string' && IMG_RE.test(e.img)) out.img = e.img;
  const j = entier(e.j, 0, JOURS - 1);
  if (j !== null) out.j = j;
  return out;
}

/* Mémoire lue : schéma 1 seulement (autre version ou JSON illisible : semaine vide), doublons et entrées invalides
   écartés, MAX entrées au plus. */
export function lireMemoire(brut) {
  let o = null;
  try { o = JSON.parse(brut); } catch (e) { return vide(); }
  if (!o || typeof o !== 'object' || o.v !== SCHEMA || !Array.isArray(o.r)) return vide();
  const vus = new Set();
  const r = [];
  for (const x of o.r) {
    const e = nettoyer(x);
    if (!e || vus.has(e.h)) continue;
    vus.add(e.h);
    r.push(e);
    if (r.length >= MAX) break;
  }
  return { v: SCHEMA, r };
}

export function serialiser(mem) {
  const r = ((mem && mem.r) || []).map(nettoyer).filter(Boolean).slice(0, MAX);
  return JSON.stringify({ v: SCHEMA, r });
}

export function contient(mem, h) { return !!(mem && Array.isArray(mem.r) && mem.r.some((e) => e.h === h)); }

export function entree(mem, h) { return (mem && mem.r ? mem.r.find((e) => e.h === h) : null) || null; }

/* Ajout en fin de semaine : { mem, ok } ; refus « invalide », « deja » ou « plein » (MAX atteint) sans rien changer. */
export function ajouter(mem, e) {
  const m = mem && Array.isArray(mem.r) ? mem : vide();
  const x = nettoyer(e);
  if (!x) return { mem: m, ok: false, raison: 'invalide' };
  if (contient(m, x.h)) return { mem: m, ok: false, raison: 'deja' };
  if (m.r.length >= MAX) return { mem: m, ok: false, raison: 'plein' };
  return { mem: { v: SCHEMA, r: m.r.concat([x]) }, ok: true };
}

export function retirer(mem, h) { return { v: SCHEMA, r: ((mem && mem.r) || []).filter((e) => e.h !== h) }; }

/* Réglage d'une recette : p (portions, ignoré hors limites), j (jour 0 à 6 ; null ou '' : pas de jour). */
export function regler(mem, h, champs) {
  const c = champs || {};
  return {
    v: SCHEMA,
    r: ((mem && mem.r) || []).map((e) => {
      if (e.h !== h) return e;
      const o = Object.assign({}, e);
      if (c.p !== undefined) { const p = entier(c.p, PORTIONS_MIN, PORTIONS_MAX); if (p !== null) o.p = p; }
      if (c.j !== undefined) { const j = entier(c.j, 0, JOURS - 1); if (j === null) delete o.j; else o.j = j; }
      return o;
    })
  };
}

/* Ordre d'affichage : par jour (lundi d'abord), recettes sans jour à la fin, puis ordre d'ajout. */
export function ordre(r) {
  return (r || []).map((e, i) => ({ e, i }))
    .sort((a, b) => (a.e.j == null ? JOURS : a.e.j) - (b.e.j == null ? JOURS : b.e.j) || a.i - b.i)
    .map((x) => x.e);
}

/* Lecture et écriture du stockage (localStorage), jamais d'exception : ok = faux si le stockage est inaccessible. */
export function charger(st) {
  try { return { mem: lireMemoire(st.getItem(CLE)), ok: true }; } catch (e) { return { mem: vide(), ok: false }; }
}

export function enregistrer(st, mem) {
  try {
    if (!mem || !mem.r || !mem.r.length) st.removeItem(CLE);
    else st.setItem(CLE, serialiser(mem));
    return true;
  } catch (e) { return false; }
}

/* Largeur demandée au CDN de Shopify (remplace width= s'il existe). */
export function taille(url, w) {
  const u = String(url == null ? '' : url).trim();
  if (!IMG_RE.test(u)) return '';
  if (/[?&]width=\d+/.test(u)) return u.replace(/([?&])width=\d+/, '$1width=' + w);
  return u + (u.indexOf('?') >= 0 ? '&' : '?') + 'width=' + w;
}

/* Entrée créée depuis la fiche : état [data-rcia-etat] (h, titre, img), type et chemin rendus par le Liquid. */
export function entreeFiche(etat, info, p) {
  if (!etat || typeof etat.h !== 'string') return null;
  const img = etat.img ? taille(etat.img, 240) : '';
  return nettoyer({ h: etat.h, type: info && info.type, url: info && info.url, titre: etat.titre, img: img || undefined, p });
}

/* Portions choisies sur la fiche : modèle de eg-rcia-fiche.js (root.__egRcia.mod.p), sinon #p=N, sinon portions de base. */
export function portionsDe(api, hash, pbase) {
  const m = api && api.mod ? entier(api.mod.p, PORTIONS_MIN, PORTIONS_MAX) : null;
  if (m !== null) return m;
  const x = /(?:^#|&)p=(\d{1,2})(?:&|$)/.exec(String(hash || ''));
  const h = x ? entier(Number(x[1]), PORTIONS_MIN, PORTIONS_MAX) : null;
  if (h !== null) return h;
  return entier(Number(pbase), PORTIONS_MIN, PORTIONS_MAX) || PORTIONS_DEFAUT;
}

/* Chemin de la fiche avec les portions de la semaine (eg-rcia-fiche.js lit #p=N). */
export function lienFiche(e) { return e && URL_RE.test(String(e.url)) ? e.url + '#p=' + e.p : ''; }

/* Libellé « Ma semaine (n) » : texte de base, nombre entre parenthèses s'il y a au moins une recette. */
export function libelleLien(base, n) { return String(base || '') + (n > 0 ? ' (' + n + ')' : ''); }

/* ---------- Liste de courses (fonctions pures) ----------
   F : module eg-rcia-fiche.js (blocDe, quantite, texteQte, texte, COMMERCES, AILLEURS). */

const pos = (x) => Number(x) > 0;
const vidOk = (v) => /^[1-9]\d{0,15}$/.test(String(v == null ? '' : v));

/* Clé d'un ingrédient sans produit : clé du dictionnaire (lot L0), sinon nom sans accents ni casse. */
export function cleTexte(l) {
  return String((l && (l.cle || l.nom)) || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim();
}

function ajouterBesoin(it, u, c, q) {
  if (q == null || !u) { it.selon = true; return; }
  const cc = pos(c) ? Number(c) : null;
  const k = u + '|' + (cc == null ? '' : cc);
  let b = it.besoins.find((x) => x.k === k);
  if (!b) { b = { k, u, c: cc, q: 0 }; it.besoins.push(b); }
  b.q += q;
}

/* Paquets d'un produit pour toute la semaine : Σ, par unité et contenance, de l'arrondi supérieur (besoins cumulés ÷
   contenance), comme paquets() de la fiche pour une recette ; 1 au moins. Ce qui n'est pas calculable (selon votre goût,
   contenance absente) ne demande qu'un paquet pour toute la semaine, jamais un par recette (huile, sel). */
export function paquetsAgreges(besoins) {
  let n = 0;
  (besoins || []).forEach((b) => { if (pos(b.c) && pos(b.q)) n += Math.ceil(b.q / b.c - 1e-9); });
  return Math.min(PAQUETS_MAX, Math.max(1, n));
}

/* Besoins regroupés par unité (contenances confondues), dans l'ordre d'apparition. */
export function unites(besoins) {
  const out = [];
  (besoins || []).forEach((b) => {
    const x = out.find((y) => y.u === b.u);
    if (x) x.q += b.q; else out.push({ u: b.u, q: b.q });
  });
  return out;
}

/* « 720 g », « 2 gousses + 1 c. à soupe », ou « selon votre goût ». */
export function texteBesoin(it, F, t, lang) {
  const parts = unites(it && it.besoins).filter((b) => b.q > 0).map((b) => F.texteQte(b.q, b.u, t, lang));
  return parts.length ? parts.join(' + ') : F.texte(t, 'fiche.selon_gout', null, lang);
}

/* Agrégation des besoins de la semaine. recettes : [{ h, p, etat, alc?: Set(id de ligne), vd?: Map(id → marque) }],
   dans l'ordre d'affichage. Statut de chaque ligne : celui de la fiche sans « J'ai déjà » (blocDe).
   - eg : produits disponibles (lignes « ok »), par variante ; n = paquetsAgreges ;
   - rupture : produits épuisés (lignes « rupture »), par variante, jamais commandables ;
   - frais : lignes « frais » (commerces, « À trouver ailleurs »), par commerce, clé et unité, quantités additionnées ;
   - deja : lignes « placard » (« Vous l'avez déjà ») ; avec un produit ITALAZA (proposé décoché) sauf si la même variante
     est déjà dans eg ou en rupture ; sans produit, une ligne texte.
   Chaque élément : { cle, type, recettes: [h], besoins: [{u, c, q}], selon, nom, … } ; produits : vid, p (carte de
   l'état), n, alc, vd ; frais : commerce. */
export function agreger(recettes, F) {
  const eg = new Map();
  const rupture = new Map();
  const deja = new Map();
  const frais = new Map();
  const comms = F.COMMERCES.concat([F.AILLEURS]);
  const noter = (table, k, base, l, r, q) => {
    let it = table.get(k);
    if (!it) { it = Object.assign({ cle: k, nom: String(l.nom || ''), besoins: [], selon: false, recettes: [] }, base); table.set(k, it); }
    ajouterBesoin(it, l.u, l.c, q);
    if (it.recettes.indexOf(r.h) < 0) it.recettes.push(r.h);
    return it;
  };
  (recettes || []).forEach((r) => {
    if (!r || !r.etat || !Array.isArray(r.etat.lignes)) return;
    r.etat.lignes.forEach((l) => {
      if (!l || typeof l !== 'object') return;
      const b = F.blocDe(l, { choix: -1, dejaOn: false });
      const q = F.quantite(l, r.p);
      const pr = l.p && typeof l.p === 'object' && vidOk(l.p.vid) ? l.p : null;
      if (pr && (b === 'ok' || b === 'rupture' || b === 'deja')) {
        const table = b === 'ok' ? eg : b === 'rupture' ? rupture : deja;
        const type = b === 'deja' ? 'deja' : b;
        const it = noter(table, 'v:' + String(pr.vid), { type, vid: String(pr.vid), p: pr, alc: false, vd: '', cles: [] }, l, r, q);
        if (typeof l.cle === 'string' && it.cles.indexOf(l.cle) < 0) it.cles.push(l.cle);
        if (r.alc && typeof r.alc.has === 'function' && r.alc.has(l.id)) it.alc = true;
        if (!it.vd && r.vd && typeof r.vd.get === 'function') it.vd = String(r.vd.get(l.id) || '');
        return;
      }
      if (b === 'deja') { noter(deja, 't:' + cleTexte(l) + '|' + (l.u || ''), { type: 'texte' }, l, r, q); return; }
      if (b !== 'frais') return; /* ligne « ok » ou « rupture » sans produit dans l'état : rien d'inventé */
      const c = comms.indexOf(l.commerce) >= 0 ? l.commerce : 'divers';
      noter(frais, c + ':' + cleTexte(l) + '|' + (l.u || ''), { type: 'frais', commerce: c }, l, r, q);
    });
  });
  eg.forEach((it, k) => { deja.delete(k); rupture.delete(k); });
  rupture.forEach((it, k) => { deja.delete(k); });
  const produits = (m) => (Array.isArray(m) ? m : Array.from(m.values()))
    .map((it) => (it.p ? Object.assign(it, { n: paquetsAgreges(it.besoins) }) : it));
  return { eg: produits(regrouperUsage(Array.from(eg.values()))), rupture: produits(rupture), frais: fraisTries(frais, comms), deja: produits(deja) };
}

function fraisTries(frais, comms) {
  return Array.from(frais.values()).map((it, i) => ({ it, i }))
    .sort((a, b) => comms.indexOf(a.it.commerce) - comms.indexOf(b.it.commerce) || a.i - b.i)
    .map((x) => x.it);
}

/* « Même usage » (16/10, demande du fondateur) : deux produits DIFFÉRENTS d'une même clé d'ingrédient interchangeable
   (vin de cuisine, huile, vinaigre, sel, sucre, farine, tomates en conserve…) ne font qu'un achat pour la semaine : le
   produit de la première recette (ordre d'affichage) reçoit les besoins de l'autre, convertis dans son unité. Jamais pour
   une clé dont les produits diffèrent par la forme ou le goût du plat (pâtes, fromages, sauces, pestos…), ni si les
   unités ne se convertissent pas (g et ml). Seulement entre produits disponibles (« ok »). */
export const MEME_USAGE = new Set([
  'vin_blanc', 'vin_rouge', 'vin_rose', 'vin_doux', 'marsala', 'rhum',
  'huile_olive', 'huile_tournesol', 'vinaigre_vin', 'vinaigre_cidre', 'vinaigre_balsamique', 'creme_balsamique',
  'sel', 'poivre', 'sucre', 'sucre_glace', 'miel', 'cacao_amer', 'cannelle', 'muscade', 'origan_seche', 'piment_seche',
  'laurier', 'vanille', 'safran',
  'farine_ble', 'farine_pizza', 'farine_complete', 'semoule_ble_dur', 'fecule_mais', 'levure_chimique', 'levure_seche',
  'chapelure', 'tomates_pelees', 'passata', 'concentre_tomate', 'tomate_cerise_boite', 'bouillon', 'cafe', 'riz_risotto',
  'polenta', 'capres', 'thon_boite',
]);
const ML_PAR = { ml: 1, cas: 15, cac: 5 };

/* Quantité q exprimée en uDe, convertie en uVers ; null si impossible. */
export function convertirUnite(q, uDe, uVers) {
  if (uDe === uVers) return q;
  if (ML_PAR[uDe] && ML_PAR[uVers]) return (q * ML_PAR[uDe]) / ML_PAR[uVers];
  return null;
}

/* Fusionne b dans a (même clé, a d'abord) ; false si une unité ne se convertit pas (rien n'est alors modifié). */
function fusionnerUsage(a, b) {
  const ref = a.besoins.find((x) => pos(x.c)) || a.besoins[0];
  if (!ref) return false;
  const ajouts = [];
  for (const x of b.besoins) {
    const q = convertirUnite(x.q, x.u, ref.u);
    if (q == null) return false;
    ajouts.push(q);
  }
  ajouts.forEach((q) => ajouterBesoin(a, ref.u, ref.c, q));
  if (b.selon) a.selon = true;
  b.recettes.forEach((h) => { if (a.recettes.indexOf(h) < 0) a.recettes.push(h); });
  a.alc = a.alc || b.alc;
  a.regroupes = (a.regroupes || []).concat(b.p && b.p.titre ? [String(b.p.titre)] : [], b.regroupes || []);
  return true;
}

/* Produits disponibles de la semaine, regroupés par même usage (voir MEME_USAGE) ; l'ordre est conservé. */
export function regrouperUsage(items) {
  const parCle = new Map();
  const garder = [];
  (items || []).forEach((it) => {
    const k = it && it.cles && it.cles.length === 1 && MEME_USAGE.has(it.cles[0]) ? it.cles[0] : null;
    const cible = k ? parCle.get(k) : null;
    if (cible && fusionnerUsage(cible, it)) return;
    if (k && !cible) parCle.set(k, it);
    garder.push(it);
  });
  return garder;
}

/* Sélection (case, paquets) de chaque produit commandable : « ok » coché, « Vous l'avez déjà » décoché, rupture jamais.
   La sélection précédente est gardée si le produit garde le même statut ; son nombre de paquets aussi, tant que le
   calcul de la semaine ne change pas (portions, recettes) : sinon il reprend le nombre calculé, comme la fiche. */
export function selection(agr, avant) {
  const sel = new Map();
  (agr.eg || []).concat(agr.deja || []).forEach((it) => {
    if (!it.p) return;
    const a = avant && avant.get(it.cle);
    const dispo = !!it.p.dispo;
    if (a && a.type === it.type) sel.set(it.cle, { type: it.type, ck: a.ck && dispo, n: a.auto === it.n ? a.n : it.n, auto: it.n });
    else sel.set(it.cle, { type: it.type, ck: it.type === 'ok' && dispo, n: it.n, auto: it.n });
  });
  return sel;
}

/* Lignes envoyées par « Tout ajouter » : produits cochés, disponibles, au moins 1 paquet ; quantités par variante. */
export function planSemaine(agr, sel) {
  const par = new Map();
  (agr.eg || []).concat(agr.deja || []).forEach((it) => {
    if (!it.p || !it.p.dispo) return;
    const s = sel.get(it.cle);
    if (!s || !s.ck || !(s.n > 0)) return;
    const k = String(it.p.vid);
    const prev = par.get(k);
    if (prev) prev.quantity += s.n;
    else par.set(k, { id: it.p.vid, quantity: s.n, prix: Number(it.p.prix) || 0, poids: Number(it.p.poids) || 0 });
  });
  return Array.from(par.values());
}

export function totalPlan(plan) { return (plan || []).reduce((s, it) => s + it.quantity * it.prix, 0); }

/* Réponse d'une fiche relue : état valide de la même recette, sinon null (h différent : autre recette, redirection). */
export function etatValide(etat, h) {
  return !!(etat && typeof etat === 'object' && etat.h === h && Array.isArray(etat.lignes));
}

/* Réduction pour tenir sur une page : premier palier qui tient, sinon le plus dense. */
export function reduction(mesurer, limite) {
  const paliers = [[], [REDUCTIONS[0]], REDUCTIONS.slice()];
  let h = 0;
  for (const cl of paliers) {
    h = mesurer(cl);
    if (h <= limite) return { classes: cl, tient: true, hauteur: h };
  }
  return { classes: paliers[paliers.length - 1], tient: false, hauteur: h };
}

/* ---------- Navigateur ---------- */

function lireJSON(el) {
  if (!el) return null;
  try { return JSON.parse(el.textContent); } catch (e) { return null; }
}

function stockage(win) {
  try { return win.localStorage; } catch (e) { return null; }
}

function setLabel(el, txt) {
  const l = el && el.querySelector('[data-l]');
  if (l) l.textContent = txt;
}

/* Liens « Ma semaine (n) » de la page Recettes et de la fiche (masqué sur la fiche tant que la semaine est vide). */
function peindreLiens(doc, mem) {
  const n = mem && mem.r ? mem.r.length : 0;
  doc.querySelectorAll('[data-eg-sem-lien], [data-eg-sem-voir]').forEach((a) => {
    setLabel(a, libelleLien(a.getAttribute('data-l-base'), n));
    if (a.hasAttribute('data-eg-sem-voir')) a.hidden = n === 0;
  });
  /* Compteurs du reste du site (en-tête, onglet Recettes, compte : eg-app.js, 16/10). */
  try { doc.dispatchEvent(new doc.defaultView.CustomEvent('eg:semaine')); } catch (e) { /* navigateur ancien */ }
}

function initLiens(doc) {
  const win = doc.defaultView;
  const lu = charger(stockage(win));
  if (!lu.ok) return;
  peindreLiens(doc, lu.mem);
  if (doc.__egSemLiens) return;
  doc.__egSemLiens = true;
  win.addEventListener('storage', (e) => { if (e.key === CLE || e.key === null) peindreLiens(doc, charger(stockage(win)).mem); });
}

/* Fiche recette : bouton « Ajouter à ma semaine » / « Dans ma semaine ✓ ». Aucune écriture sans clic. */
function initFiche(root) {
  const btn = root.querySelector('[data-eg-sem-act]');
  if (!btn || btn.__egSem) return;
  btn.__egSem = true;
  const doc = root.ownerDocument;
  const win = doc.defaultView;
  const etat = lireJSON(root.querySelector('script[data-rcia-etat]'));
  if (!etat || typeof etat.h !== 'string') return;
  const info = { type: btn.getAttribute('data-type'), url: btn.getAttribute('data-url') };
  const L = (k) => btn.getAttribute('data-l-' + k) || '';
  if (!charger(stockage(win)).ok) return; /* pas de stockage : rien ne serait gardé, pas de bouton */
  const toast = (m, undo, err, lien) => { const E = win.EG; if (E && typeof E.toast === 'function') E.toast(m, undo || null, !!err, 0, lien || null); };
  /* « Voir ma semaine → » dans le message d'ajout (16/10) : où retrouver la recette. */
  const voir = root.querySelector('[data-eg-sem-voir]');
  const lienSemaine = voir && voir.getAttribute('href') ? { href: voir.getAttribute('href'), label: L('voir') } : null;
  const lire = () => charger(stockage(win)).mem;

  function peindre(mem) {
    const dans = contient(mem, etat.h);
    setLabel(btn, dans ? L('dans') : L('ajouter'));
    /* Le libellé porte l'état (comme « J'ai déjà » de la fiche) ; dans la semaine, le nom dit aussi que le clic retire. */
    if (dans) btn.setAttribute('aria-label', L('dans-aria')); else btn.removeAttribute('aria-label');
    btn.querySelectorAll('[data-eg-sem-ic]').forEach((i) => { i.hidden = i.getAttribute('data-eg-sem-ic') !== (dans ? 'ok' : 'plus'); });
    btn.classList.toggle('is-on', dans);
    peindreLiens(doc, mem);
  }
  function sauver(mem) {
    if (!enregistrer(stockage(win), mem)) { toast(L('stockage'), null, true); return false; }
    peindre(mem);
    return true;
  }

  btn.addEventListener('click', (e) => {
    e.preventDefault();
    const mem = lire(); /* relue : un autre onglet a pu la changer */
    if (contient(mem, etat.h)) {
      const avant = entree(mem, etat.h);
      if (sauver(retirer(mem, etat.h))) {
        toast(L('retiree'), () => { const r = ajouter(lire(), avant); if (r.ok) sauver(r.mem); else peindre(lire()); });
      }
      return;
    }
    const r = ajouter(mem, entreeFiche(etat, info, portionsDe(root.__egRcia, win.location.hash, etat.pbase)));
    if (!r.ok) {
      if (r.raison === 'plein') toast(L('pleine'), null, true);
      peindre(mem);
      return;
    }
    if (sauver(r.mem)) toast(L('ajoutee'), () => sauver(retirer(lire(), etat.h)), false, lienSemaine);
  });

  /* Portions changées sur la fiche (− + de eg-rcia-fiche.js) : la semaine suit, si la recette y est déjà. */
  root.addEventListener('click', (e) => {
    if (!e.target.closest || !e.target.closest('[data-rcia-p]')) return;
    win.setTimeout(() => {
      const mem = lire();
      const cur = entree(mem, etat.h);
      if (!cur) return;
      const p = portionsDe(root.__egRcia, win.location.hash, etat.pbase);
      if (cur.p !== p) enregistrer(stockage(win), regler(mem, etat.h, { p }));
    }, 0);
  });
  win.addEventListener('storage', (e) => { if (e.key === CLE || e.key === null) peindre(lire()); });
  peindre(lire());
  btn.hidden = false;
}

/* ---------- Page « Ma semaine » ---------- */

function initPage(root) {
  if (root.__egSem) return;
  root.__egSem = true;
  const cfg = lireJSON(root.querySelector('script[data-eg-sem-config]')) || {};
  const s = cfg.s && typeof cfg.s === 'object' ? cfg.s : {};
  const msg = root.querySelector('[data-eg-sem-msg]');
  const src = typeof cfg.fiche === 'string' && /^(https:)?\/\/[^\s"'<>\\]+$/.test(cfg.fiche) ? cfg.fiche : '';
  const echec = () => {
    if (msg) { msg.textContent = typeof s.erreur === 'string' ? s.erreur : ''; msg.hidden = !msg.textContent; }
    const ld = root.querySelector('[data-eg-sem-load]');
    if (ld) ld.hidden = true;
  };
  if (!src) { echec(); return; }
  import(src).then((F) => page(root, cfg, F)).catch((e) => { console.warn('[eg-semaine] module de la fiche indisponible', e); echec(); });
}

function page(root, cfg, F) {
  const doc = root.ownerDocument;
  const win = doc.defaultView;
  const t = cfg.t && typeof cfg.t === 'object' ? cfg.t : {};
  const s = cfg.s && typeof cfg.s === 'object' ? cfg.s : {};
  const x = cfg.x && typeof cfg.x === 'object' ? cfg.x : {};
  const egcfg = lireJSON(doc.getElementById('eg-config')) || {};
  const lang = doc.documentElement.getAttribute('lang') || 'fr';
  const T = (k, v) => F.texte(t, k, v, lang);
  const S = (k, v) => F.texte(s, k, v, lang);
  const X = (k) => (typeof x[k] === 'string' ? x[k] : '');
  const langFr = cfg.lang_fr === true;
  const eg = () => win.EG || null;
  const money = (c) => { const E = eg(); return E && typeof E.money === 'function' ? E.money(c) : F.money(c); };
  const toast = (m, undo, err, ms) => { const E = eg(); if (E && typeof E.toast === 'function') E.toast(m, undo || null, !!err, ms); };
  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const q1 = (sel) => root.querySelector(sel);

  const els = {
    msg: q1('[data-eg-sem-msg]'),
    vide: q1('[data-eg-sem-vide]'),
    blocR: q1('[data-eg-sem-bloc="recettes"]'),
    blocC: q1('[data-eg-sem-bloc="courses"]'),
    liste: q1('[data-eg-sem-recettes]'),
    nb: q1('[data-eg-sem-nb]'),
    load: q1('[data-eg-sem-load]'),
    titreR: q1('[data-eg-sem-bloc="recettes"] h2'),
    h1: q1('h1')
  };
  const groupe = (g) => q1('[data-eg-sem-g="' + g + '"]');

  const st = stockage(win);
  const lu = charger(st);
  let mem = lu.mem;
  const etats = new Map(); /* h → { statut: 'charge' | 'ok' | 'erreur', etat, alc, vd } */
  let agr = { eg: [], rupture: [], frais: [], deja: [] };
  let sel = new Map();
  let occupe = false;
  const messages = [];

  function dire(m) {
    if (!els.msg || !m) return;
    if (messages.indexOf(m) < 0) messages.push(m);
    els.msg.textContent = messages.join(' ');
    els.msg.hidden = false;
  }
  if (!lu.ok) dire(S('sans_stockage'));

  function sauver() { if (!enregistrer(st, mem)) dire(S('sans_stockage')); peindreLiens(doc, mem); }

  const icone = (nom) => {
    const tpl = doc.getElementById('eg-icons');
    const i = tpl && tpl.content ? tpl.content.querySelector('[data-i="' + nom + '"]') : null;
    return i ? i.innerHTML.trim() : '';
  };
  const el = (tag, cls, txt) => {
    const e = doc.createElement(tag);
    if (cls) e.className = cls;
    if (txt != null) e.textContent = txt;
    return e;
  };
  const bouton = (cls, attr, val, label, ic) => {
    const b = el('button', cls);
    b.type = 'button';
    b.setAttribute(attr, val);
    if (label) b.setAttribute('aria-label', label);
    if (ic) b.insertAdjacentHTML('beforeend', icone(ic)); /* balisage du gabarit #eg-icons (serveur) */
    return b;
  };
  const enFr = (e) => { if (langFr) e.setAttribute('lang', 'fr'); return e; };

  /* ---------- Recettes de la semaine ---------- */
  function ligneRecette(e) {
    const titre = e.titre || e.h;
    const li = el('li', 'eg-sem-r');
    li.setAttribute('data-eg-sem-h', e.h);
    const href = lienFiche(e);
    const ph = el('a', 'eg-sem-ri');
    ph.setAttribute('href', href);
    ph.tabIndex = -1;
    ph.setAttribute('aria-hidden', 'true');
    if (e.img) {
      const im = el('img');
      im.setAttribute('src', e.img);
      im.setAttribute('alt', '');
      im.setAttribute('width', '72');
      im.setAttribute('height', '72');
      im.setAttribute('loading', 'lazy');
      ph.appendChild(im);
    }
    li.appendChild(ph);
    const c = el('div', 'eg-sem-rc');
    const a = enFr(el('a', 'eg-sem-rt', titre));
    a.setAttribute('href', href);
    c.appendChild(a);
    const rs = el('span', 'eg-sem-rs');
    rs.setAttribute('data-eg-sem-rs', '');
    rs.hidden = true;
    c.appendChild(rs);
    const rr = el('div', 'eg-sem-rr');
    const sj = el('select', 'eg-sem-j');
    sj.setAttribute('data-eg-sem-jour', '');
    sj.setAttribute('aria-label', S('jour_pour', { titre }));
    const o0 = el('option', null, S('jour_aucun'));
    o0.value = '';
    sj.appendChild(o0);
    for (let j = 0; j < JOURS; j++) {
      const o = el('option', null, S('jour_' + (j + 1)));
      o.value = String(j);
      sj.appendChild(o);
    }
    sj.value = e.j == null ? '' : String(e.j);
    rr.appendChild(sj);
    const step = el('span', 'step eg-sem-step');
    step.setAttribute('role', 'group');
    step.setAttribute('aria-label', S('portions_pour', { titre }));
    step.appendChild(bouton('', 'data-eg-sem-p', '-1', X('p_moins'), 'minus'));
    const out = el('output', null, T('fiche.valeur', { n: e.p, count: e.p }));
    out.setAttribute('data-eg-sem-pv', '');
    out.setAttribute('aria-live', 'polite');
    step.appendChild(out);
    step.appendChild(bouton('', 'data-eg-sem-p', '+1', X('p_plus'), 'plus'));
    rr.appendChild(step);
    const bx = bouton('eg-sem-x', 'data-eg-sem-x', '', S('retirer_nom', { titre }), 'trash');
    bx.appendChild(el('span', null, S('retirer')));
    rr.appendChild(bx);
    c.appendChild(rr);
    li.appendChild(c);
    return li;
  }

  function majRecette(li, e) {
    const out = li.querySelector('[data-eg-sem-pv]');
    if (out) out.textContent = T('fiche.valeur', { n: e.p, count: e.p });
    li.querySelectorAll('[data-eg-sem-p]').forEach((b) => {
      const plus = b.getAttribute('data-eg-sem-p') === '+1';
      b.disabled = plus ? e.p >= PORTIONS_MAX : e.p <= PORTIONS_MIN;
    });
    const href = lienFiche(e);
    li.querySelectorAll('a.eg-sem-ri, a.eg-sem-rt').forEach((a) => a.setAttribute('href', href));
    const sj = li.querySelector('[data-eg-sem-jour]');
    const v = e.j == null ? '' : String(e.j);
    if (sj && sj.value !== v) sj.value = v;
    const et = etats.get(e.h);
    const rs = li.querySelector('[data-eg-sem-rs]');
    if (rs) {
      const err = !!(et && et.statut === 'erreur');
      rs.textContent = err ? S('charge_erreur', { titre: e.titre || e.h }) : '';
      rs.hidden = !err;
    }
    li.classList.toggle('is-load', !et || et.statut === 'charge');
  }

  function rendreRecettes() {
    const liste = ordre(mem.r);
    const n = liste.length;
    if (els.vide) els.vide.hidden = n > 0;
    if (els.blocR) els.blocR.hidden = n === 0;
    if (els.blocC) els.blocC.hidden = n === 0;
    if (els.nb) els.nb.textContent = n ? S('nb', { n, count: n }) : '';
    if (!els.liste) return;
    const actif = doc.activeElement;
    const lis = liste.map((e) => {
      let li = els.liste.querySelector('[data-eg-sem-h="' + e.h + '"]');
      if (!li) li = ligneRecette(e);
      majRecette(li, e);
      return li;
    });
    const deja = Array.from(els.liste.children);
    if (deja.length !== lis.length || deja.some((c, i) => c !== lis[i])) {
      els.liste.replaceChildren(...lis);
      if (actif && actif.isConnected && actif !== doc.activeElement) actif.focus({ preventScroll: true });
    }
    barre(n > 0);
  }

  /* ---------- Relecture des fiches ---------- */
  async function lireFiche(e) {
    const essais = [e.url + '?section_id=eg-recette', e.url];
    for (let i = 0; i < essais.length; i++) {
      const page = i === essais.length - 1;
      let r;
      try { r = await win.fetch(essais[i], { headers: { Accept: 'text/html' }, credentials: 'same-origin' }); } catch (err) { if (page) return { statut: 'erreur' }; continue; }
      if (!r.ok) {
        if (page) return { statut: r.status === 404 || r.status === 410 ? 'absent' : 'erreur' };
        continue;
      }
      let d = null;
      try { d = new win.DOMParser().parseFromString(await r.text(), 'text/html'); } catch (err) { d = null; }
      const etat = d ? lireJSON(d.querySelector('script[data-rcia-etat]')) : null;
      if (etatValide(etat, e.h)) {
        /* Marque et message sanitaire de chaque ligne, lus sur la carte rendue par la fiche (rien n'est déduit ici). */
        const alc = new Set();
        const vd = new Map();
        etat.lignes.forEach((l) => {
          if (!l || typeof l.id !== 'string' || !/^[a-z][a-z0-9_]{0,15}$/.test(l.id)) return;
          const row = d.querySelector('[data-rl="' + l.id + '"]');
          if (!row) return;
          if (row.querySelector('.eg-alc')) alc.add(l.id);
          const v = row.querySelector('.vd');
          if (v) vd.set(l.id, v.textContent.replace(/\s+/g, ' ').trim());
        });
        return { statut: 'ok', etat, alc, vd };
      }
      if (page) return { statut: etat && typeof etat.h === 'string' ? 'absent' : 'erreur' };
    }
    return { statut: 'erreur' };
  }

  function chargerTout() {
    const a = mem.r.filter((e) => !etats.has(e.h));
    if (!a.length) { recalculer(); return; }
    a.forEach((e) => etats.set(e.h, { statut: 'charge' }));
    rendreRecettes();
    if (els.load) els.load.hidden = false;
    Promise.all(a.map((e) => lireFiche(e).then((res) => ({ e, res }), () => ({ e, res: { statut: 'erreur' } })))).then((rs) => {
      rs.forEach(({ e, res }) => {
        if (res.statut === 'absent') {
          etats.delete(e.h);
          mem = retirer(mem, e.h);
          dire(S('absente', { titre: e.titre || e.h }));
        } else etats.set(e.h, res);
      });
      if (rs.some((x) => x.res.statut === 'absent')) sauver();
      rendreRecettes();
      recalculer();
    });
  }

  /* ---------- Liste de courses ---------- */
  function recettesChargees() {
    return ordre(mem.r).map((e) => ({ e, et: etats.get(e.h) }))
      .filter((y) => y.et && y.et.statut === 'ok')
      .map((y) => ({ h: y.e.h, p: y.e.p, etat: y.et.etat, alc: y.et.alc, vd: y.et.vd }));
  }

  function recalculer() {
    agr = agreger(recettesChargees(), F);
    sel = selection(agr, sel);
    const enCours = mem.r.some((e) => { const et = etats.get(e.h); return !et || et.statut === 'charge'; });
    if (els.load) els.load.hidden = !enCours;
    rendreCourses();
    majBarre();
    majImpression();
  }

  function rangerDans(cont, rows) {
    const actif = doc.activeElement;
    const avant = Array.from(cont.children);
    if (avant.length !== rows.length || avant.some((c, i) => c !== rows[i])) cont.replaceChildren(...rows);
    if (actif && actif.isConnected && actif !== doc.activeElement) actif.focus({ preventScroll: true });
  }

  function ligneProduit(it) {
    const row = el('div', 'row eg-sem-pl');
    row.setAttribute('data-eg-sem-k', it.cle);
    const href = F.urlSure(it.p.url);
    const ph = el('a', 'ph');
    if (href) ph.setAttribute('href', href);
    ph.tabIndex = -1;
    ph.setAttribute('aria-hidden', 'true');
    const src = F.sized(it.p.img, 176);
    if (src) {
      const im = el('img');
      im.setAttribute('src', src);
      im.setAttribute('alt', '');
      im.setAttribute('width', '88');
      im.setAttribute('height', '88');
      im.setAttribute('loading', 'lazy');
      ph.appendChild(im);
    }
    row.appendChild(ph);
    const inn = el('div', 'in');
    const need = el('span', 'eg-sem-need');
    need.setAttribute('data-eg-sem-need', '');
    inn.appendChild(need);
    const nm = el('a', 'nm', F.nomCourt(it.p.titre));
    if (href) nm.setAttribute('href', href);
    inn.appendChild(nm);
    if (it.vd) inn.appendChild(el('span', 'vd', it.vd));
    if (it.p.qline) inn.appendChild(el('span', 'qt', String(it.p.qline)));
    if (it.regroupes && it.regroupes.length && S('meme_usage')) {
      inn.appendChild(el('span', 'eg-sem-usage', S('meme_usage', { titre: it.regroupes.map((x) => F.nomCourt(x)).join(', ') })));
    }
    if (it.alc && X('sante')) inn.appendChild(el('p', 'eg-sem-alc1', X('sante')));
    if (!it.p.dispo) inn.appendChild(el('span', 'eg-sem-soon', X('epuise')));
    row.appendChild(inn);
    const rt = el('div', 'rt');
    const lab = el('label', 'eg-sem-ck');
    const ck = el('input');
    ck.type = 'checkbox';
    ck.setAttribute('data-eg-sem-ck', '');
    lab.appendChild(ck);
    lab.appendChild(el('span', 'eg-sr', T('fiche.case', { produit: F.nomCourt(it.p.titre) })));
    rt.appendChild(lab);
    rt.appendChild(el('span', 'pr', money(it.p.prix)));
    if (it.type !== 'rupture') {
      const step = el('span', 'step eg-sem-q');
      step.appendChild(bouton('', 'data-eg-sem-q', '-1', T('fiche.qte_moins'), 'minus'));
      const out = el('output', null, '0');
      out.setAttribute('data-eg-sem-n', '');
      out.setAttribute('aria-live', 'polite');
      step.appendChild(out);
      step.appendChild(bouton('', 'data-eg-sem-q', '+1', T('fiche.qte_plus'), 'plus'));
      rt.appendChild(step);
    }
    row.appendChild(rt);
    return row;
  }

  function majProduit(row, it) {
    row.setAttribute('data-st', it.type);
    const nr = it.recettes.length;
    const need = row.querySelector('[data-eg-sem-need]');
    if (need) need.textContent = S('besoin', { q: texteBesoin(it, F, t, lang), n: nr, count: nr });
    const sl = sel.get(it.cle);
    const actif = !!(it.p.dispo && it.type !== 'rupture' && sl);
    const ck = row.querySelector('[data-eg-sem-ck]');
    if (ck) { ck.disabled = !actif; ck.checked = !!(actif && sl.ck); }
    const out = row.querySelector('[data-eg-sem-n]');
    if (out) out.textContent = String(sl ? sl.n : 0);
    row.querySelectorAll('[data-eg-sem-q]').forEach((b) => {
      const plus = b.getAttribute('data-eg-sem-q') === '+1';
      b.disabled = !(actif && sl.ck) || (plus ? sl.n >= PAQUETS_MAX : sl.n <= 0);
    });
  }

  function ligneTexte(it) {
    const row = el('div', 'eg-sem-t');
    row.setAttribute('data-eg-sem-k', it.cle);
    row.appendChild(enFr(el('span', 'eg-sem-tn', it.nom)));
    const q = el('span', 'eg-sem-tq');
    q.setAttribute('data-eg-sem-need', '');
    row.appendChild(q);
    return row;
  }

  function lignes(cont, items) {
    const rows = items.map((it) => {
      let row = cont.querySelector('[data-eg-sem-k="' + (win.CSS && win.CSS.escape ? win.CSS.escape(it.cle) : it.cle) + '"]');
      if (row && (!!it.p) !== row.classList.contains('eg-sem-pl')) row = null;
      if (!row) row = it.p ? ligneProduit(it) : ligneTexte(it);
      if (it.p) majProduit(row, it);
      else row.querySelector('[data-eg-sem-need]').textContent = texteBesoin(it, F, t, lang);
      return row;
    });
    rangerDans(cont, rows);
  }

  function rendreCourses() {
    ['eg', 'rupture', 'deja'].forEach((g) => {
      const box = groupe(g);
      if (!box) return;
      const items = agr[g] || [];
      box.hidden = !items.length;
      const cont = box.querySelector('[data-eg-sem-liste]');
      if (cont) lignes(cont, items);
    });
    const alc = q1('[data-eg-sem-alc]');
    if (alc) alc.hidden = !(agr.eg || []).concat(agr.deja || []).some((it) => it.alc);
    const fb = groupe('frais');
    if (!fb) return;
    fb.hidden = !agr.frais.length;
    const cont = fb.querySelector('[data-eg-sem-liste]');
    if (!cont) return;
    const comms = F.COMMERCES.concat([F.AILLEURS]);
    const blocs = [];
    comms.forEach((c) => {
      const items = agr.frais.filter((it) => it.commerce === c);
      if (!items.length) return;
      let b = cont.querySelector('[data-eg-sem-com="' + c + '"]');
      if (!b) {
        b = el('div', 'eg-sem-com');
        b.setAttribute('data-eg-sem-com', c);
        b.appendChild(el('h4', null, T('commerce.' + c)));
        if (c === F.AILLEURS && X('ailleurs_aide')) b.appendChild(el('p', 'eg-sem-com-aide', X('ailleurs_aide')));
        const l = el('div', 'eg-sem-tl');
        l.setAttribute('data-eg-sem-tl', '');
        b.appendChild(l);
      }
      lignes(b.querySelector('[data-eg-sem-tl]'), items);
      blocs.push(b);
    });
    rangerDans(cont, blocs);
  }

  /* ---------- Barre « Tout ajouter » (comme la fiche) ---------- */
  function barre(montrer) {
    let b = root.querySelector('[data-eg-sem-bar]');
    if (!montrer) { if (b) b.remove(); return null; }
    if (!b) {
      const tpl = root.querySelector('template[data-eg-sem-bar-tpl]');
      if (!tpl) return null;
      root.appendChild(tpl.content.cloneNode(true));
      b = root.querySelector('[data-eg-sem-bar]');
      majBarre();
    }
    return b;
  }

  const panier = { total: num(egcfg.total), poids: num(egcfg.weight) };

  function majBarre() {
    const b = root.querySelector('[data-eg-sem-bar]');
    if (!b) return;
    const plan = planSemaine(agr, sel);
    const tot = totalPlan(plan);
    const add = b.querySelector('[data-eg-sem-add]');
    if (add) {
      setLabel(add, plan.length ? T('fiche.tout_ajouter', { prix: money(tot) }) : T('fiche.rien'));
      if (!occupe) add.disabled = !plan.length;
    }
    const te = b.querySelector('[data-eg-sem-total]');
    if (te) te.textContent = money(tot);
    const jz = b.querySelector('[data-eg-sem-jauge]');
    if (!jz) return;
    /* Seuil lu dans #eg-config seulement (R15 de la fiche) : sans seuil, pas de jauge. */
    const seuil = num(egcfg.free);
    if (egcfg.gauge !== true || !(seuil > 0)) { jz.hidden = true; return; }
    let g = jz.querySelector('.gauge');
    let tx = jz.querySelector('small');
    if (!g) { g = el('div', 'gauge'); g.appendChild(el('span')); jz.appendChild(g); }
    if (!tx) { tx = el('small'); jz.appendChild(tx); }
    const pmax = num(cfg.poids_max);
    const E = eg();
    const hp = F.horsPanier(plan, E && typeof E.qty === 'function' ? (k) => E.qty(k) : null);
    const j = F.jauge(panier.total, hp.montant, seuil, panier.poids + hp.poids, pmax);
    g.firstElementChild.style.width = j.pct + '%';
    tx.textContent = j.lourd ? T('fiche.poids', { max: F.nombre(pmax / 1000) }) : j.offerte ? T('fiche.jauge_ok') : T('fiche.jauge_reste', { montant: money(j.reste) });
    jz.hidden = false;
  }

  async function relirePanier(E) {
    if (typeof E.refresh !== 'function') return null;
    try { return F.qtesPanier(await E.refresh()); } catch (e) { return null; }
  }

  /* Même déroulé que « Tout ajouter » de la fiche (eg-rcia-fiche.js) : panier relu avant et après, reprise produit par
     produit si l'envoi groupé est refusé, bilan mesuré, « Annuler » 10 s qui ne retire que ce qui a vraiment été ajouté. */
  async function toutAjouter(btn) {
    if (occupe) return;
    const E = eg();
    if (!E || typeof E.addMany !== 'function' || typeof E.qty !== 'function') return;
    const plan = planSemaine(agr, sel).map((it) => ({ id: it.id, quantity: it.quantity }));
    if (!plan.length) { toast(T('fiche.rien')); return; }
    occupe = true;
    const avaitFocus = doc.activeElement === btn;
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    try {
      const lu0 = await relirePanier(E);
      const avant = new Map(plan.map((it) => [String(it.id), lu0 ? lu0.get(String(it.id)) || 0 : E.qty(it.id)]));
      let apres = null;
      try {
        await E.addMany(plan);
        apres = await relirePanier(E);
      } catch (err) {
        apres = await relirePanier(E);
        if (apres) {
          for (const it of plan) {
            const k = String(it.id);
            if ((apres.get(k) || 0) > avant.get(k)) continue;
            try { await E.addMany([it]); } catch (e2) { /* variante non ajoutée */ }
          }
          apres = await relirePanier(E);
        }
      }
      if (!apres) { toast(T('fiche.non_relu'), null, true); return; }
      const b = F.bilanAjout(plan, avant, (k) => apres.get(k) || 0);
      if (!b.ok) toast(T('fiche.aucun_ajout'), null, true);
      else {
        let fait = false;
        const annuler = async () => {
          if (fait) return;
          fait = true;
          const maintenant = await relirePanier(E);
          if (!maintenant) { toast(T('fiche.non_relu'), null, true); return; }
          for (const c of F.ciblesAnnulation(b.ajoutes, (k) => maintenant.get(k) || 0)) {
            try { await E.setQty(c.vid, c.q); } catch (e4) { /* message générique d'eg-app.js */ }
          }
          toast(T('fiche.annule'));
        };
        toast(b.ko ? T('fiche.partiel', { ok: b.ok, ko: b.ko }) : T('fiche.ajoutes', { n: b.ok, count: b.ok }), annuler, false, 10000);
      }
    } finally {
      occupe = false;
      btn.removeAttribute('aria-busy');
      majBarre();
      if (avaitFocus && btn.isConnected && !btn.disabled) btn.focus({ preventScroll: true });
    }
  }

  /* ---------- Impression (A4, une page, sans prix) ---------- */
  let rp = q1('[data-eg-sem-print-bloc]');
  if (rp) {
    const ancien = doc.getElementById('eg-sem-rp');
    if (ancien && ancien !== rp) ancien.remove();
    rp.id = 'eg-sem-rp';
    rp.hidden = true;
    rp.setAttribute('aria-hidden', 'true');
    doc.body.appendChild(rp);
    doc.documentElement.classList.add('eg-sem-rp-on');
  }

  function liCourse(qtxt, nom, sous) {
    const li = el('li');
    if (qtxt) li.appendChild(el('b', null, qtxt + ' '));
    li.appendChild(enFr(el('span', null, nom)));
    if (sous) li.appendChild(el('small', null, sous));
    return li;
  }

  function contenuImpression() {
    const f = doc.createDocumentFragment();
    const hd = el('header', 'eg-sem-rp-hd');
    hd.appendChild(el('h1', null, S('print_titre')));
    if (cfg.hote) hd.appendChild(el('p', 'eg-sem-rp-hote', String(cfg.hote)));
    f.appendChild(hd);
    const menu = el('section', 'eg-sem-rp-menu');
    menu.appendChild(el('h2', null, S('print_menu')));
    const ul = el('ul');
    ordre(mem.r).forEach((e) => {
      const li = el('li');
      li.appendChild(el('b', null, (e.j == null ? S('jour_aucun') : S('jour_' + (e.j + 1))) + ' · '));
      li.appendChild(enFr(el('span', null, e.titre || e.h)));
      li.appendChild(el('small', null, T('print.pour', { n: e.p, count: e.p })));
      ul.appendChild(li);
    });
    menu.appendChild(ul);
    f.appendChild(menu);
    const cols = el('section', 'eg-sem-rp-cols');
    cols.appendChild(el('h2', null, T('print.courses')));
    const grp = (titre, items, ligne) => {
      if (!items.length) return;
      const g = el('div', 'eg-sem-rp-g');
      g.appendChild(el('h3', null, titre));
      const l = el('ul');
      items.forEach((it) => l.appendChild(ligne(it)));
      g.appendChild(l);
      cols.appendChild(g);
    };
    const qt = (it) => { const v = texteBesoin(it, F, t, lang); return it.besoins.some((b) => b.q > 0) ? v : ''; };
    const sousProduit = (it) => (it.p ? F.nomCourt(it.p.titre) : '');
    grp(T('print.eg'), agr.eg, (it) => liCourse(qt(it), it.nom, sousProduit(it)));
    grp(X('bloc_rupture'), agr.rupture, (it) => liCourse(qt(it), it.nom, sousProduit(it)));
    grp(T('print.frais'), agr.frais, (it) => liCourse(qt(it), it.nom, T('commerce.' + it.commerce)));
    grp(T('print.deja'), agr.deja, (it) => liCourse(qt(it), it.nom, sousProduit(it)));
    f.appendChild(cols);
    return f;
  }

  const mmPx = 96 / 25.4;
  function mesurer(classes) {
    const c = rp.cloneNode(true);
    c.removeAttribute('id');
    c.hidden = false;
    REDUCTIONS.forEach((k) => c.classList.remove(k));
    c.classList.add('eg-sem-rp-mesure');
    classes.forEach((k) => c.classList.add(k));
    doc.body.appendChild(c);
    const h = c.getBoundingClientRect().height;
    c.remove();
    return h;
  }

  function majImpression() {
    if (!rp) return;
    rp.replaceChildren(contenuImpression());
    const res = reduction(mesurer, A4.hauteurMm * mmPx);
    REDUCTIONS.forEach((k) => rp.classList.toggle(k, res.classes.indexOf(k) >= 0));
    rp.setAttribute('data-rp-tient', res.tient ? 'oui' : 'non');
  }

  /* ---------- Actions ---------- */
  function focusApresRetrait(h) {
    const lis = els.liste ? Array.from(els.liste.children) : [];
    const i = lis.findIndex((li) => li.getAttribute('data-eg-sem-h') === h);
    return (i >= 0 && (lis[i + 1] || lis[i - 1])) || null;
  }

  function retirerRecette(h) {
    const avant = entree(mem, h);
    if (!avant) return;
    const voisin = focusApresRetrait(h);
    mem = retirer(mem, h);
    sauver();
    rendreRecettes();
    recalculer();
    const cible = (voisin && voisin.isConnected && voisin.querySelector('a.eg-sem-rt')) || (mem.r.length ? els.titreR : els.h1);
    if (cible) { if (!cible.hasAttribute('tabindex') && cible.tagName !== 'A') cible.setAttribute('tabindex', '-1'); cible.focus({ preventScroll: false }); }
    toast(S('retiree'), () => {
      const r = ajouter(charger(st).mem, avant);
      if (!r.ok) return;
      mem = r.mem;
      sauver();
      rendreRecettes();
      chargerTout();
    });
  }

  function onClick(e) {
    const b = e.target.closest && e.target.closest('[data-eg-sem-p],[data-eg-sem-x],[data-eg-sem-q],[data-eg-sem-add],[data-eg-sem-print]');
    if (!b || b.disabled || !root.contains(b)) return;
    e.preventDefault();
    if (b.hasAttribute('data-eg-sem-add')) { toutAjouter(b); return; }
    if (b.hasAttribute('data-eg-sem-print')) { majImpression(); win.print(); return; }
    if (b.hasAttribute('data-eg-sem-q')) {
      const row = b.closest('[data-eg-sem-k]');
      const k = row && row.getAttribute('data-eg-sem-k');
      const sl = k && sel.get(k);
      if (!sl || !sl.ck) return;
      sl.n = Math.min(PAQUETS_MAX, Math.max(0, sl.n + (b.getAttribute('data-eg-sem-q') === '+1' ? 1 : -1)));
      if (sl.n === 0) sl.ck = false;
      const it = agr.eg.concat(agr.deja).find((y) => y.cle === k);
      if (it) majProduit(row, it);
      majBarre();
      return;
    }
    const li = b.closest('[data-eg-sem-h]');
    const h = li && li.getAttribute('data-eg-sem-h');
    const cur = h && entree(mem, h);
    if (!cur) return;
    if (b.hasAttribute('data-eg-sem-x')) { retirerRecette(h); return; }
    const p = Math.min(PORTIONS_MAX, Math.max(PORTIONS_MIN, cur.p + (b.getAttribute('data-eg-sem-p') === '+1' ? 1 : -1)));
    if (p === cur.p) return;
    mem = regler(mem, h, { p });
    sauver();
    rendreRecettes();
    recalculer();
  }

  function onChange(e) {
    const c = e.target;
    if (c.matches && c.matches('[data-eg-sem-ck]')) {
      const row = c.closest('[data-eg-sem-k]');
      const k = row && row.getAttribute('data-eg-sem-k');
      const sl = k && sel.get(k);
      const it = k && agr.eg.concat(agr.deja).find((y) => y.cle === k);
      if (!sl || !it) return;
      sl.ck = !!c.checked && !!it.p.dispo;
      if (sl.ck && sl.n === 0) sl.n = it.n;
      majProduit(row, it);
      majBarre();
      return;
    }
    if (c.matches && c.matches('[data-eg-sem-jour]')) {
      const li = c.closest('[data-eg-sem-h]');
      const h = li && li.getAttribute('data-eg-sem-h');
      if (!h || !entree(mem, h)) return;
      mem = regler(mem, h, { j: c.value === '' ? null : Number(c.value) });
      sauver();
      rendreRecettes();
      recalculer();
    }
  }

  root.addEventListener('click', onClick);
  root.addEventListener('change', onChange);
  doc.addEventListener('eg:cart', (e) => {
    const c = (e && e.detail) || {};
    if (c.total_price != null) panier.total = num(c.total_price);
    if (c.total_weight != null) panier.poids = num(c.total_weight);
    majBarre();
  });
  win.addEventListener('storage', (e) => {
    if (e.key !== CLE && e.key !== null) return;
    mem = charger(st).mem;
    Array.from(etats.keys()).forEach((h) => { if (!contient(mem, h)) etats.delete(h); });
    rendreRecettes();
    chargerTout();
  });
  win.addEventListener('beforeprint', () => majImpression());

  rendreRecettes();
  chargerTout();
}

/* Démarrage automatique dans le navigateur (et relance dans l'éditeur de thème). */
if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  const boot = (scope) => {
    const s = scope || document;
    s.querySelectorAll('[data-rcia-fiche]').forEach((r) => initFiche(r));
    s.querySelectorAll('[data-eg-sem]').forEach((r) => initPage(r));
    initLiens(document);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
}
